/**
 * Whether this document is on its way out: a full-page navigation the shell started itself
 * (`leavePage`) or one the browser announced (`beforeunload`, `pagehide`).
 *
 * Leaving a page cancels every `<script>` still loading, and WebKit and Firefox report each
 * cancelled script as a load `error` the moment the navigation starts: before `beforeunload`
 * in WebKit, one millisecond after it in Firefox, and about a second before `pagehide` in
 * both. Chrome reports nothing. So a script error is not a failure while the page is
 * leaving, and only the code that starts the navigation knows that in time (TFLS-3279).
 */

/**
 * How long a navigation counts as in progress. The cancellations arrive within
 * milliseconds; the window only has to outlive them. It is short on purpose: a
 * `beforeunload` is also what a `mailto:` link or a download started through the location
 * leaves behind on a page that then stays, and real failures must be reported again soon.
 */
export const LEAVE_WINDOW_MS = 5_000;

let leftAt: number | undefined;
let hidden = false;

export function isLeavingPage(now: number = Date.now()): boolean {
  if (hidden) return true;
  if (leftAt === undefined) return false;
  const elapsed = now - leftAt;
  // Negative when the wall clock stepped back after the mark. The page then counts as
  // staying: a real failure reported once too often beats every failure dropped for as
  // long as the clock takes to catch up.
  return elapsed >= 0 && elapsed <= LEAVE_WINDOW_MS;
}

/** Marks the page as leaving without navigating; `leavePage` and the listeners use it. */
export function markLeavingPage(now: number = Date.now()): void {
  leftAt = now;
}

export interface LeavePageOptions {
  /** `location.replace` instead of `location.href`: the current entry leaves the history. */
  replace?: boolean;
}

/**
 * The one way the shell navigates to another document: the IdP, an external deep link, a
 * hard reload of `/login`. Marks the page first, so that what the navigation cancels is
 * not mistaken for a failure.
 */
export function leavePage(url: string, { replace = false }: LeavePageOptions = {}): void {
  markLeavingPage();
  if (replace) {
    window.location.replace(url);
  } else {
    window.location.href = url;
  }
}

/**
 * Listens for navigations the shell did not start: the user typing a url, a widget assigning
 * `location`, the tab closing. `pagehide` is definitive until a `pageshow` brings the
 * document back from the back/forward cache. Returns the function that removes the
 * listeners.
 */
export function trackPageUnload(target: Window = window): () => void {
  const onBeforeUnload = () => {
    markLeavingPage();
  };
  const onPageHide = () => {
    hidden = true;
  };
  const onPageShow = (event: Event) => {
    if ((event as PageTransitionEvent).persisted) {
      hidden = false;
      leftAt = undefined;
    }
  };
  target.addEventListener('beforeunload', onBeforeUnload);
  target.addEventListener('pagehide', onPageHide);
  target.addEventListener('pageshow', onPageShow);
  return () => {
    target.removeEventListener('beforeunload', onBeforeUnload);
    target.removeEventListener('pagehide', onPageHide);
    target.removeEventListener('pageshow', onPageShow);
  };
}
